function errorBlob(error: unknown): { code: string; text: string } {
  if (!error || typeof error !== "object") return { code: "", text: "" }
  const o = error as { message?: string; code?: string; details?: string; hint?: string }
  return {
    code: String(o.code ?? ""),
    text: `${o.message ?? ""} ${o.details ?? ""} ${o.hint ?? ""}`.toLowerCase(),
  }
}

/**
 * Detects PostgREST / Postgres errors when a table or view is not in the DB
 * (migrations not applied) or not in the schema cache yet.
 */
export function isMissingDatabaseRelation(error: unknown): boolean {
  const { code, text } = errorBlob(error)
  // PostgREST: table not exposed / not in cache; Postgres: undefined_table
  if (code === "PGRST205" || code === "42P01") return true
  if (!text.trim()) return false
  if (/could not find the table/i.test(text)) return true
  if (/could not find.*schema cache/i.test(text) && /table/i.test(text)) return true
  if (/relation .+ does not exist/i.test(text)) return true
  return false
}

/**
 * Detects PostgREST / Postgres errors when a column is missing from the live schema
 * or schema cache (optional migrations not applied).
 *
 * When `columnNames` is provided, also matches those names in the error text.
 */
export function isMissingDatabaseColumn(error: unknown, columnNames?: string[]): boolean {
  const { code, text } = errorBlob(error)
  // PostgREST: column not in schema cache; Postgres: undefined_column
  if (code === "PGRST204" || code === "42703") {
    if (!columnNames?.length) return true
    return columnNames.some((c) => text.includes(c.toLowerCase()))
  }
  if (!text.trim()) return false
  if (/could not find the '[^']+' column/i.test(text)) {
    if (!columnNames?.length) return true
    return columnNames.some((c) => text.includes(c.toLowerCase()))
  }
  if (/column .+ does not exist/i.test(text)) {
    if (!columnNames?.length) return true
    return columnNames.some((c) => text.includes(c.toLowerCase()))
  }
  return false
}

/** True when the error indicates a missing table/view or a missing column. */
export function isMissingSchemaObject(error: unknown): boolean {
  return isMissingDatabaseRelation(error) || isMissingDatabaseColumn(error)
}
